import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { code: "", discount_percent: "", discount_fixed: "", active: true };

export default function Promos() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [type, setType] = useState("percent");
  const { data: items = [] } = useQuery({ queryKey: ["promos"], queryFn: () => api.get("/promos").then((r) => r.data) });

  const open = (p) => {
    if (p) { setEditing(p); setType(p.discount_fixed ? "fixed" : "percent"); }
    else { setEditing({ ...EMPTY }); setType("percent"); }
  };

  const save = async () => {
    const body = {
      code: editing.code,
      active: editing.active,
      discount_percent: type === "percent" ? Number(editing.discount_percent) || null : null,
      discount_fixed: type === "fixed" ? Number(editing.discount_fixed) || null : null,
    };
    try {
      if (editing.id) await api.put(`/promos/${editing.id}`, body);
      else await api.post("/promos", body);
      qc.invalidateQueries({ queryKey: ["promos"] });
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete promo?")) return;
    await api.delete(`/promos/${id}`);
    qc.invalidateQueries({ queryKey: ["promos"] });
    toast.success("Deleted");
  };

  return (
    <div data-testid="admin-promos">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-white mb-1">Promo Codes</h1><p className="text-white/50 text-sm">Percent or fixed-dollar discounts, validated at checkout.</p></div>
        <button onClick={() => open(null)} data-testid="add-promo-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Plus size={15} /> Add</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p, i) => (
          <div key={p.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 flex justify-between items-start" data-testid={`promo-${i}`}>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-white tracking-wide">{p.code}</h3>
                <span className={`text-[10px] rounded-full px-2 ${p.active ? "bg-green-500/20 text-green-400" : "bg-white/10 text-white/40"}`}>{p.active ? "active" : "off"}</span>
              </div>
              <p className="text-[#D4AF37] text-sm mt-2">{p.discount_percent ? `${p.discount_percent}% off` : `$${p.discount_fixed} off`}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => open(p)} data-testid={`edit-promo-${i}`} className="text-white/50 hover:text-[#D4AF37] p-1.5"><Pencil size={15} /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-promo-${i}`} className="text-white/50 hover:text-red-400 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} Promo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Input placeholder="CODE" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className="bg-[#0a0a0a] border-white/15 uppercase tracking-wide" data-testid="promo-code" />
              <div className="flex gap-2">
                <button onClick={() => setType("percent")} className={`flex-1 rounded-lg py-2 text-sm ${type === "percent" ? "btn-gold" : "border border-white/15 text-white/60"}`}>Percent %</button>
                <button onClick={() => setType("fixed")} className={`flex-1 rounded-lg py-2 text-sm ${type === "fixed" ? "btn-gold" : "border border-white/15 text-white/60"}`}>Fixed $</button>
              </div>
              {type === "percent" ? (
                <Input type="number" placeholder="e.g. 15" value={editing.discount_percent || ""} onChange={(e) => setEditing({ ...editing, discount_percent: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="promo-percent" />
              ) : (
                <Input type="number" placeholder="e.g. 50" value={editing.discount_fixed || ""} onChange={(e) => setEditing({ ...editing, discount_fixed: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="promo-fixed" />
              )}
              <div className="flex items-center gap-3"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><span className="text-sm text-white/70">Active</span></div>
              <button onClick={save} data-testid="save-promo-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
